import { memo } from 'react'
import { Quote, Trash2, AlertTriangle, Check, CheckCheck } from 'lucide-react'
import { cn, formatDate, gravatarUrl, escapeHtml } from '@/lib/utils'
import type { ChatMessage as ChatMessageType } from '@/types/chat'

/**
 * ChatMessage component - displays a single chat message.
 *
 * Features:
 * - Different styling for own vs other messages
 * - Online status indicator
 * - Read/unread status
 * - Actions (quote, delete, report)
 * - Emoji support (basic text emojis)
 */

interface ChatMessageProps {
  message: ChatMessageType
  isOwnMessage: boolean
  onQuote?: (messageId: string) => void
  onDelete?: (messageId: string) => void
}

// Memoize to prevent unnecessary re-renders
export const ChatMessage = memo(function ChatMessage({
  message,
  isOwnMessage,
  onQuote,
  onDelete,
}: ChatMessageProps) {
  const sender = message.sender

  // Process message content (convert URLs to links, emojis, etc.)
  const processedContent = processMessageContent(message.content)

  return (
    <div
      className={cn(
        'group flex gap-3 px-4 py-2 transition-colors',
        'hover:bg-gray-50/50',
        isOwnMessage && 'flex-row-reverse'
      )}
      data-message-id={message.id}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <a href={`/user/${sender.username}`} target="_blank" rel="noopener noreferrer">
          <img
            src={gravatarUrl(sender.gravatarHash, 40)}
            alt={sender.username}
            className="h-10 w-10 rounded-full"
          />
        </a>
        {/* Online indicator */}
        <span
          className={cn(
            'absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white',
            message.online ? 'bg-success' : 'bg-gray-400'
          )}
          title={message.online ? 'Online' : 'Offline'}
        />
      </div>

      {/* Message bubble */}
      <div
        className={cn(
          'flex max-w-[70%] flex-col gap-1',
          isOwnMessage ? 'items-end' : 'items-start'
        )}
      >
        {/* Header - sender name and time */}
        <div className={cn('flex items-center gap-2 text-xs', isOwnMessage && 'flex-row-reverse')}>
          <a
            href={`/user/${sender.username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-text-dark hover:text-primary"
          >
            {sender.username}
          </a>

          {/* Premium/Sponsor badges */}
          {sender.userCapabilities?.premium && (
            <span className="text-yellow-500" title="Premium">
              ★
            </span>
          )}

          <span className="text-text-muted">{formatDate(message.dateSent)}</span>
        </div>

        {/* Message content */}
        <div
          className={cn(
            'rounded-2xl px-4 py-2',
            isOwnMessage
              ? 'rounded-tr-sm bg-primary text-white'
              : 'rounded-tl-sm bg-gray-100 text-text-dark'
          )}
        >
          <p
            className="whitespace-pre-wrap break-words text-sm"
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />
        </div>

        {/* Actions and read status */}
        <div
          className={cn(
            'flex items-center gap-2 text-xs text-text-muted',
            'opacity-0 transition-opacity group-hover:opacity-100',
            isOwnMessage && 'flex-row-reverse'
          )}
        >
          {/* Read status for own messages */}
          {isOwnMessage && (
            <span title={message.read ? 'Read' : 'Delivered'}>
              {message.read ? (
                <CheckCheck className="h-4 w-4 text-primary" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </span>
          )}

          {/* Quote action */}
          {onQuote && (
            <button
              onClick={() => onQuote(message.id)}
              className="rounded p-1 hover:bg-gray-200"
              title="Quote"
            >
              <Quote className="h-4 w-4" />
            </button>
          )}

          {/* Delete action (only for own messages) */}
          {isOwnMessage && onDelete && (
            <button
              onClick={() => onDelete(message.id)}
              className="rounded p-1 text-danger hover:bg-danger-50"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}

          {/* Report action (only for other's messages) */}
          {!isOwnMessage && (
            <button className="rounded p-1 hover:bg-gray-200" title="Report">
              <AlertTriangle className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
})

/**
 * Process message content - convert URLs to links and text emojis to icons.
 */
function processMessageContent(content: string): string {
  // First escape HTML to prevent XSS
  let processed = escapeHtml(content)

  // Convert URLs to links
  const urlRegex = /(\bhttps?:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;[\]]*[-A-Z0-9+&@#/%=~_|])/gi
  processed = processed.replace(
    urlRegex,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="underline">$1</a>'
  )

  // Convert newlines to <br>
  processed = processed.replace(/\n/g, '<br />')

  // Basic emoji replacements (matches v1 behavior)
  const emojis: Record<string, string> = {
    ':-\\)': '😊',
    ';-\\)': '😉',
    ':-\\(': '😢',
    ':-P': '😛',
    ':-D': '😄',
    ':-O': '😮',
    '\\*_\\*': '🤩',
    ':-\\/': '🙄',
    ':-\\*': '😘',
    '&lt;3': '❤️',
  }

  Object.entries(emojis).forEach(([pattern, emoji]) => {
    const regex = new RegExp(`( |^)(${pattern})( |$)`, 'gi')
    processed = processed.replace(regex, `$1${emoji}$3`)
  })

  return processed
}

/**
 * Date separator component for showing dates between message groups.
 */
export function DateSeparator({ date }: { date: string }) {
  return (
    <div className="flex items-center gap-4 py-4">
      <div className="h-px flex-1 bg-gray-200" />
      <span className="text-xs font-medium text-text-muted">
        {new Date(date).toLocaleDateString('de-DE', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}
      </span>
      <div className="h-px flex-1 bg-gray-200" />
    </div>
  )
}
